// Image resolver (Unsplash integration optional and disabled by default)
// Exports:
// - resolveImageUrl (sync fallback — returns original URL if not a placeholder)
// - resolveImageUrlAsync (async — optionally fetches a real Unsplash photo URL for placeholders)
// - useResolvedImageUrl (React hook — resolves asynchronously and updates when ready)
// - ResolvedImg (React component — drop-in <img> with async resolving)

import React, { useEffect, useMemo, useState } from 'react';
// Note: We avoid importing unsplash-js at module load to prevent bundle/runtime
// issues in some browsers/extensions. If enabled via env, we will dynamic-import it.

const PLACEHOLDER_HOSTS = new Set([
  'placehold.co',
  'via.placeholder.com',
  'dummyimage.com',
  'fakeimg.pl'
]);

function isPlaceholder(url) {
  try {
    const u = new URL(url);
    return PLACEHOLDER_HOSTS.has(u.hostname);
  } catch (e) {
    return false;
  }
}

function buildQuery(text, keywords = []) {
  const parts = [];
  if (text) parts.push(String(text));
  if (Array.isArray(keywords)) {
    for (const k of keywords) if (k) parts.push(String(k));
  }
  const q = parts.join(',').trim();
  return q || 'immigration,travel,city';
}

// Feature flag: enable Unsplash resolving (disabled by default for stability)
// Set REACT_APP_ENABLE_UNSPLASH=true to turn on.
const ENABLE_UNSPLASH = String(process.env.REACT_APP_ENABLE_UNSPLASH || '').toLowerCase() === 'true';
const ACCESS_KEY = process.env.REACT_APP_UNSPLASH_ACCESS_KEY || '';
let _unsplash = null; // lazy-loaded client
async function getUnsplash() {
  if (!_unsplash) {
    try {
      const mod = await import('unsplash-js');
      _unsplash = mod.createApi({ accessKey: ACCESS_KEY });
    } catch (e) {
      _unsplash = null;
    }
  }
  return _unsplash;
}

// In-memory cache to avoid repeated requests within the same session
const urlCache = new Map(); // key: cacheKey -> value: resolved image URL

function sizeKey(width, height) {
  const w = Number(width) || 1200;
  const h = Number(height) || 400;
  const orient = w >= h ? 'landscape' : 'portrait';
  let variant = 'regular';
  if (w <= 400 || h <= 300) variant = 'small';
  if (w <= 200 || h <= 150) variant = 'thumb';
  return { w, h, orient, variant };
}

/**
 * Synchronous fallback — returns the original url when not a placeholder.
 * For placeholders, returns the placeholder immediately (so UI renders),
 * while async helpers below can be used to fetch a real Unsplash image.
 */
export function resolveImageUrl(url) {
  if (!url) return url;
  if (!isPlaceholder(url)) return url;
  return url; // immediate fallback; use async functions for actual Unsplash URL
}

/**
 * Resolve to a real Unsplash image URL using unsplash-js for placeholder inputs.
 */
export async function resolveImageUrlAsync(url, { width = 1200, height = 400, text, keywords = [] } = {}) {
  if (!url) return url;
  if (!isPlaceholder(url)) return url;
  // If Unsplash resolving is disabled or no access key, return placeholder as-is
  if (!ENABLE_UNSPLASH || !ACCESS_KEY) return url;

  const query = buildQuery(text, keywords);
  const { orient, variant } = sizeKey(width, height);
  const cacheKey = `${query}::${orient}::${variant}`;
  if (urlCache.has(cacheKey)) return urlCache.get(cacheKey);

  try {
    const client = await getUnsplash();
    if (!client) return url;
    const result = await client.photos.getRandom({ query, orientation: orient, contentFilter: 'high', count: 1 });
    const photo = Array.isArray(result?.response) ? result.response[0] : result?.response;
    const resolved = photo?.urls?.[variant] || photo?.urls?.regular || photo?.urls?.small || url;
    if (resolved) urlCache.set(cacheKey, resolved);
    return resolved;
  } catch (e) {
    // On error, fall back to original url (placeholder)
    return url;
  }
}

/**
 * React hook that resolves placeholder images using Unsplash asynchronously.
 */
export function useResolvedImageUrl(url, options = {}) {
  const { width = 1200, height = 400, text, keywords = [] } = options;
  const initial = useMemo(() => resolveImageUrl(url), [url]);
  const [resolved, setResolved] = useState(initial);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const finalUrl = await resolveImageUrlAsync(url, { width, height, text, keywords });
      if (!cancelled) setResolved(finalUrl || initial);
    })();
    return () => {
      cancelled = true;
    };
  }, [url, width, height, text, Array.isArray(keywords) ? keywords.join('|') : String(keywords)]);

  return resolved;
}

/**
 * Convenience React component that renders an <img> and resolves Unsplash URL under the hood.
 */
export function ResolvedImg({ src, width = 1200, height = 400, text, keywords = [], alt = '', className, loading = 'lazy', decoding = 'async', style }) {
  const resolvedSrc = useResolvedImageUrl(src, { width, height, text, keywords });
  return (
    <img src={resolvedSrc} alt={alt} className={className} loading={loading} decoding={decoding} style={style} />
  );
}

export default resolveImageUrl;
